import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Pagination,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // Import Close Icon
import { ReactComponent as LeftArrowIcon } from "../../images/leftArrowIcon.svg";
import { ReactComponent as ResetIcon } from "../../images/resetIcon.svg";
import { useLocation, useNavigate } from "react-router-dom";

function TestcaseSelection(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [selectedTestCaseIndex, setSelectedTestCaseIndex] = useState(null); // Stores the index of the selected test case
  const [currentPage, setCurrentPage] = useState(1);
  const [testCaseData, setTestCaseData] = useState({});
  const [uploadKey, setUploadKey] = useState(0); // Key to force re-render

  const testCasesTitles = props.testCases.map(
    (testCase) => testCase["Test Case Title"]
  );
  const itemsPerPage = 8;

  const handleTestCaseChange = (event) => {
    const index = parseInt(event.target.value, 10); // Parse the value to an integer
    setSelectedTestCaseIndex(index); // Store the index of the selected test case

    // Log the corresponding object to the console
    const selectedObject = props.testCases[index];
    setTestCaseData(selectedObject);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const resetFilters = () => {
    setSelectedTestCaseIndex(null);
    setCurrentPage(1);
    setTestCaseData({}); // Clear test case data
    setUploadKey((prevKey) => prevKey + 1); // Force re-render by changing the key
  };

  const handleClose = () => {
    setOpen(false); // Close the dialog
  };

  // const navigateToFrameWorkPage = () => {
  //   navigate("/dashboard/test-automation/PageSource", {
  //     state: { testCaseData: testCaseData },
  //   });
  // };

  const openTestCaseImportedPage = (e) => {
    e.preventDefault();
    
    // Validate that a test case is selected
    if (!testCaseData || Object.keys(testCaseData).length === 0) {
      alert("Please select a test case first.");
      return;
    }
    
    // Update parent component state
    props.setTestCase(testCaseData);
    
    // Navigate with the selected test case data
    navigate(location.pathname, {
      state: { ...location.state, testCaseData: testCaseData },
    });
    setOpen(false);
  };

  // Calculate the test cases to display on the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTestCases = testCasesTitles.slice(startIndex, endIndex);

  const buttons = [
    {
      label: "Reset Filters",
      onClick: resetFilters, // Function reference
      variant: "text",
      startIcon: <ResetIcon />,
      sx: { textTransform: "none" },
    },
    {
      label: "Import Test Case",
      onClick: openTestCaseImportedPage,
      variant: "contained",
      disabled: !testCaseData || Object.keys(testCaseData).length === 0,
      sx: { float: "right", textTransform: "none" },
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: "500px", // Fixed width
          height: "600px", // Fixed height
          padding: "20px",
          overflow: "hidden", // Removes scrollbar
        },
      }}
      key={uploadKey} // Reset the component when uploadKey changes
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}
      >
        {/* Left Section */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={handleClose} sx={{ marginRight: "10px" }}>
            <LeftArrowIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Roboto, sans-serif",
              marginLeft: "-14px",
            }}
          >
            Import Test Case
          </Typography>
        </div>

        {/* Right Close Button */}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>

      <Typography
        sx={{
          fontSize: "14px",
          fontFamily: "Roboto, sans-serif",
          paddingLeft: "25px",
          opacity: 0.6,
        }}
      >
        Select one test case to be automated
      </Typography>

      <DialogContent
        sx={{
          height: "calc(100% - 120px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // Pushes pagination to the bottom
          paddingBottom: "0px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto", // Scroll only this section
            paddingRight: "10px", // Optional: helps avoid scrollbar overlay
          }}
        >
          <FormControl component="fieldset">
            <RadioGroup
              value={
                selectedTestCaseIndex !== null
                  ? selectedTestCaseIndex.toString()
                  : ""
              }
              onChange={handleTestCaseChange}
            >
              {paginatedTestCases.map((testCase, index) => (
                <FormControlLabel
                  key={startIndex + index}
                  value={(startIndex + index).toString()}
                  control={<Radio sx={{ fontSize: "10px" }} />}
                  label={testCase}
                  sx={{
                    fontSize: "14px",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            paddingX: 2,
            marginTop: 2,
            marginBottom: 5,
            gap: 6,
          }}
        >
          <Typography sx={{ fontSize: "13px", opacity: 0.6 }}>
            {`${paginatedTestCases.length}/${testCasesTitles.length} Items`}
          </Typography>

          <Pagination
            count={Math.ceil(testCasesTitles.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            size="small"
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{ justifyContent: "space-between", padding: "10px 24px" }}
      >
        {buttons.map((button, index) => (
          <Button
            key={index}
            onClick={button.onClick}
            variant={button.variant}
            startIcon={button.startIcon}
            sx={button.sx}
            disabled={button.disabled}
          >
            {button.label}
          </Button>
        ))}
      </DialogActions>
    </Dialog>
  );
}

export default TestcaseSelection;
